'use client';

import { formatDistanceToNow } from 'date-fns';
import { Antenna } from 'lucide-react';
import { EmptyState, ErrorState, LoadingState } from '@/components/shared';
import {
  Badge,
  Card,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui';
import { usePartnerChannels } from '@/hooks/usePartners';

/**
 * The partner's channels, as its token sees them. Synced, never authored here —
 * a channel the token can no longer see is kept and marked dead, because
 * devices still point at it.
 */
export function ChannelsCard({ partnerId }: { partnerId: string }) {
  const { data, isLoading, error } = usePartnerChannels(partnerId);

  if (isLoading) return <LoadingState />;
  if (error) return <ErrorState title="Could not load channels" message={error.message} />;

  const channels = data?.data ?? [];

  if (channels.length === 0) {
    return (
      <EmptyState
        icon={Antenna}
        title="No channels"
        description="Store the Flespi token and sync; the channels the token can reach land here."
      />
    );
  }

  return (
    <Card>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Channel</TableHead>
            <TableHead>Protocol</TableHead>
            <TableHead>Devices</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Synced</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {channels.map((channel) => (
            <TableRow key={channel.id}>
              <TableCell>
                <p className="font-medium">{channel.name}</p>
                <p className="text-sm text-muted-foreground">#{channel.providerChannelId}</p>
              </TableCell>
              <TableCell className="text-sm text-muted-foreground">
                {channel.protocolName ?? '—'}
              </TableCell>
              <TableCell>{channel.devices}</TableCell>
              <TableCell>
                <Badge variant={channel.isActive ? 'success' : 'secondary'}>
                  {channel.isActive ? 'Connected' : 'Gone'}
                </Badge>
              </TableCell>
              <TableCell className="text-sm text-muted-foreground">
                {formatDistanceToNow(new Date(channel.syncedAt), { addSuffix: true })}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}
